import type { Proposal } from "@stockline/shared/types/ai/proposal";
import type { PreOrder } from "@/app/api/types/preOrder";
import { isProposalPreOrder } from "../types/proposal.types";

export interface PreOrderProposalContext {
  batchId: string;
  proposalId: string;
  proposal: Proposal;
}

/**
 * Extract the parent `pre_order` Proposal from a proposal-annotated PreOrder
 * so callers can route header/flow edits to the proposal payload instead of
 * the live entity. Returns `null` when the PreOrder isn't proposal-annotated
 * or when the batch contains no matching `pre_order` proposal.
 *
 * Prefers `annotation.sourceProposalId` (set when the annotation was built
 * from a specific proposal) to avoid routing edits to the wrong proposal
 * when a batch carries multiple `pre_order` entries.
 */
export function findPreOrderProposal(
  preOrder: PreOrder,
): PreOrderProposalContext | null {
  if (!isProposalPreOrder(preOrder)) return null;
  const annotation = preOrder.__proposal;
  const proposal = annotation.sourceProposalId
    ? (annotation.proposals.find(
        (p) =>
          p.id === annotation.sourceProposalId && p.entityType === "pre_order",
      ) ?? null)
    : (annotation.proposals.find((p) => p.entityType === "pre_order") ?? null);
  if (!proposal) return null;
  return { batchId: annotation.batchId, proposalId: proposal.id, proposal };
}
